import React from "react";
import { View, StyleSheet, Platform, ScrollView } from "react-native";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import { EditTaskBlue, TrashCanBlue } from "../../components/svgs/common";
import { File } from "../../components/svgs/CustomCoursework";
import { WebView } from "react-native-webview";
import { getEmbeddedVideoUrl } from "../../utils/regex.dictionary";
import CustomHtml from "../../components/common/general/CustomHtml/CustomHtml";
import useFileFetch from "../../hooks/useFileFetch";
import { Assessment } from "@axio/graphql/generated/graphql";
import { normalizeObjectives } from "../../utils/objectives";

const BUTTON_HEIGHT = 36;

function ActivityContainer({ activity, onEdit, onDelete }) {
  const { fetchAndOpenFile } = useFileFetch();
  const normalizedObjectives = React.useMemo(
    () =>
      normalizeObjectives({
        activityId: activity?.id,
        objectives: activity?.objectives,
      }),
    [activity?.id, activity?.objectives]
  );

  if (!activity) {
    return (
      <View style={styles.emptyContainer}>
        <CustomText text="No activity selected" style={styles.emptyText} />
      </View>
    );
  }

  const getVisualizationHtml = (visualization) => {
    // Simplify the meta tag a bit to avoid potential blocking issues.
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <meta http-equiv="Content-Security-Policy" content="default-src * 'unsafe-inline' 'unsafe-eval' data: gap:">
          <script src="https://cdn.jsdelivr.net/npm/p5@1.4.1/lib/p5.js"></script>
          <style>
            html, body {
              margin: 0;
              padding: 0;
              width: 100%;
              height: 100vh;
              display: flex;
              justify-content: center;
              align-items: center;
              background-color: #EAECF0;
              overflow: hidden;
            }
            #visualization-container {
              width: 100%;
              height: 100%;
            }
          </style>
        </head>
        <body>
          <div id="visualization-container"></div>
          <script>
            try {
              ${visualization.code}
            } catch (e) {
              console.error('p5.js Error:', e);
            }
          </script>
        </body>
      </html>
    `;
  };

  return (
    <View style={styles.container}>
      <View style={styles.activityHeader}>
        <View style={{ flexDirection: "column", flex: 1, flexShrink: 1 }}>
          <CustomText text={activity.type} size="s" weight="medium" style={{ color: "#667085" }} />
          <CustomText
            text={activity.title}
            weight="bold"
            size="xl"
            useTranslationText={false}
            style={{ flexShrink: 1 }}
          />
        </View>
        <View style={styles.buttonContainer}>
          <CustomButton
            text="Edit"
            styleType="primaryTransparentStroke"
            style={styles.generalButtonStyle}
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            onPress={onEdit}
            size="s"
            leftIcon={<EditTaskBlue width={16} height={16} style={{ marginRight: 4 }} />}
          />
          <CustomSpacing type="horizontal" size="m" />
          <CustomButton
            text="Delete"
            styleType="primaryTransparentStroke"
            style={styles.generalButtonStyle}
            onPress={onDelete}
            size="s"
            leftIcon={<TrashCanBlue width={16} height={16} />}
            textStyle={{ fontSize: 14, fontWeight: 700 }}
          />
        </View>
      </View>

      <CustomSpacing type="vertical" size="m" />

      {/* Activity Instructions */}
      {activity.type !== "Assessment" && activity.type !== "Review" && (
        <>
          <CustomText text="Description" weight="ultraBold" size="m" style={{ color: "#182230" }} />

          <CustomSpacing type="vertical" size="xl" />

          <View style={{ flex: 1 }}>
            <CustomHtml html={activity?.description || "<p>No content available</p>"} allowEmbeds />
          </View>

          <CustomSpacing type="vertical" size="xl" />
        </>
      )}

      {/* Only show Learning Objectives for non-Resource types */}
      {activity.type !== "Resource" &&
        activity.type !== "Assignment" &&
        activity.type !== "Assessment" &&
        activity.type !== "Project" &&
        activity.type !== "Review" && (
          <>
            <CustomText text="Learning Objectives" weight="ultraBold" size="m" style={{ color: "#182230" }} />
            <CustomSpacing type="vertical" size="xl" />
            {normalizedObjectives.length > 0 ? (
              normalizedObjectives.map((obj, index) => (
                <View key={obj.id}>
                  <CustomText
                    text={`${index + 1}. ${obj.objective}`}
                    size="m"
                    weight="normal"
                    style={styles.text}
                    useTranslationText={false}
                  />
                  <CustomSpacing type="vertical" size="xs" />
                </View>
              ))
            ) : (
              <CustomText
                text="No learning objectives available"
                size="m"
                weight="normal"
                style={styles.text}
                useTranslationText={false}
              />
            )}
            <CustomSpacing type="vertical" size="xl" />
          </>
        )}

      {/* Due Date for Interactive Learning Experience */}
      {activity.type === "Interactive Learning Experience" && activity.dueDate && (
        <>
          <CustomText text="Due Date" weight="ultraBold" size="m" />
          <CustomSpacing type="vertical" size="s" />
          <CustomText
            useTranslationText={false}
            text={new Date(activity.dueDate).toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
            size="m"
          />
          <CustomSpacing type="vertical" size="xl" />
        </>
      )}

      {normalizedObjectives.some((obj) => obj.videos?.length > 0) && (
        <>
          <CustomText text="Recommended Videos" weight="ultraBold" size="m" style={{ color: "#182230" }} />
          <CustomSpacing type="vertical" size="xl" />

          {normalizedObjectives.map((obj) =>
            obj.videos?.length > 0 ? (
              <View key={obj.id} style={{ marginBottom: 32 }}>
                <CustomText
                  text={`🎯 ${obj.objective}`}
                  size="m"
                  weight="bold"
                  style={{ marginBottom: 12 }}
                  useTranslationText={false}
                />

                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.videoRow} tabIndex={0}>
                  {obj.videos.map((video) => (
                    <View key={video.id} style={styles.videoCard}>
                      <CustomText
                        text={video.title}
                        size="s"
                        weight="medium"
                        style={{ marginBottom: 8, maxWidth: 300 }}
                        numberOfLines={2}
                        ellipsizeMode="tail"
                        useTranslationText={false}
                      />

                      {Platform.OS === "web" ? (
                        <WebView
                          style={styles.webview}
                          javaScriptEnabled
                          domStorageEnabled
                          source={{ uri: getEmbeddedVideoUrl(video.videoId) }}
                        />
                      ) : (
                        <CustomButton onPress={() => getEmbeddedVideoUrl(video.url)} style={styles.thumbnailButton}>
                          <img src={video.thumbnailUrl} alt={video.title} style={styles.thumbnailImage} />
                        </CustomButton>
                      )}
                    </View>
                  ))}
                </ScrollView>

                <CustomSpacing type="vertical" size="xl" />
              </View>
            ) : null
          )}
        </>
      )}

      {/* Interactive Visualization Section */}
      {activity.visualization && activity.visualization.code && (
        <View style={{}}>
          <CustomSpacing type="horizontal" size="m" />
          <CustomText text={"Interactive Visualization"} size="m" weight="ultraBold" style={{ color: "#182230" }} />
          <CustomSpacing type="horizontal" size="xl" />
          <View style={styles.visualizationContainer}>
            <View style={styles.webViewContainer}>
              <WebView
                source={{ html: getVisualizationHtml(activity.visualization) }}
                originWhitelist={["*"]}
                style={styles.webView}
                javaScriptEnabled={true}
                domStorageEnabled={true}
                onError={(syntheticEvent) => {
                  const { nativeEvent } = syntheticEvent;
                  console.error("WebView error:", nativeEvent);
                }}
                onHttpError={(syntheticEvent) => {
                  const { nativeEvent } = syntheticEvent;
                  console.error("WebView HTTP error:", nativeEvent);
                }}
                startInLoadingState={true}
              />
            </View>
          </View>
        </View>
      )}

      {/* Files section */}
      {activity.type === "Resource" && activity.files?.length > 0 && (
        <>
          <CustomText text="Files" weight="ultraBold" size="m" style={{ color: "#182230" }} />
          <CustomSpacing type="vertical" size="xl" />
          {activity.files.map((file, index) => (
            <View key={index} style={styles.fileContainer}>
              <File width={16} height={16} style={{ paddingRight: 8 }} />
              <CustomText
                text={file.fileName}
                weight="bold"
                style={styles.fileText}
                useTranslationText={false}
                onPress={() => fetchAndOpenFile(file.fileURL)}
              />
            </View>
          ))}
          <CustomSpacing type="vertical" size="xl" />
        </>
      )}

      {/* Show Video URL for Resource type */}
      {activity.type === "Resource" && activity.videoUrl && (
        <>
          <CustomText text="Video URL" weight="ultraBold" size="m" style={{ color: "#182230" }} />
          <CustomSpacing type="vertical" size="xl" />

          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "flex-start",
            }}>
            <View
              style={{
                width: "30%",
                marginRight: 10,
                marginBottom: 10,
                aspectRatio: 16 / 9,
                borderRadius: 8,
                overflow: "hidden",
              }}>
              <WebView
                source={{ uri: getEmbeddedVideoUrl(activity.videoUrl) }}
                style={{ flex: 1 }}
                allowsFullscreenVideo
                javaScriptEnabled
                domStorageEnabled
              />
            </View>
          </View>

          <CustomSpacing type="vertical" size="xl" />
        </>
      )}

      {/* === Assignment Specific Info === */}
      {activity.type === "Assignment" && (
        <>
          {/* Due Date */}
          {activity.due_date && (
            <>
              <CustomText text="Due Date" weight="ultraBold" size="m" style={{ color: "#182230" }} />
              <CustomSpacing type="vertical" size="xl" />
              <CustomText
                useTranslationText={false}
                text={new Date(activity.due_date).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
                size="m"
              />
              <CustomSpacing type="vertical" size="xl" />
            </>
          )}

          {/* Video Preview */}
          {activity.videoUrl && (
            <>
              <CustomText text="Video" weight="ultraBold" size="m" style={{ color: "#182230" }} />
              <CustomSpacing type="vertical" size="xl" />

              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  justifyContent: "flex-start",
                }}>
                <View
                  style={{
                    width: "30%",
                    marginRight: 10,
                    marginBottom: 10,
                    aspectRatio: 16 / 9,
                    borderRadius: 8,
                    overflow: "hidden",
                  }}>
                  <WebView
                    source={{ uri: getEmbeddedVideoUrl(activity.videoUrl) }}
                    javaScriptEnabled
                    domStorageEnabled
                    allowsFullscreenVideo
                    style={{ flex: 1 }}
                  />
                </View>
              </View>
            </>
          )}

          {/* File Upload Toggle */}
          <CustomText text="File Upload Enabled" weight="ultraBold" size="m" style={{ color: "#182230" }} />
          <CustomSpacing type="vertical" size="xl" />
          <CustomText
            useTranslationText={false}
            text={activity.enableStudentFileUpload ? "Yes" : "No"}
            weight="normal"
            size="m"
          />
          <CustomSpacing type="vertical" size="xl" />

          {/* File Attachments */}
          {activity.files?.length > 0 && (
            <>
              <CustomText text="Attached Files" weight="ultraBold" size="m" style={{ color: "#182230" }} />
              <CustomSpacing type="vertical" size="xl" />
              {activity.files.map((file, index) => (
                <View key={index} style={styles.fileContainer}>
                  <File width={24} height={24} style={{ paddingRight: 10 }} />
                  <CustomText
                    text={file.fileName}
                    useTranslationText={false}
                    style={styles.fileText}
                    weight="ultraBold"
                    onPress={() => fetchAndOpenFile(file.fileUrl)}
                  />
                </View>
              ))}
              <CustomSpacing type="vertical" size="xl" />
            </>
          )}
        </>
      )}

      {/* === Assessment Specific Info === */}
      {activity.type === "Assessment" && (
        <>
          <CustomText text="Number of questions" weight="ultraBold" size="m" style={{ color: "#182230" }} />
          <CustomSpacing type="vertical" size="xl" />
          <CustomText
            text={activity?.numberOfQuestions || "10"}
            useTranslationText={false}
            style={styles.text}
            weight="normal"
          />
          <CustomSpacing type="vertical" size="xl" />
          <CustomText text="Time limit" weight="ultraBold" size="m" style={{ color: "#182230" }} />
          <CustomSpacing type="vertical" size="xl" />
          <CustomText
            text="assessment_activity_time_limit"
            interpolation={{ timeLimit: activity?.timeLimit || "25" }}
            style={styles.text}
            weight="normal"
          />
          <CustomSpacing type="vertical" size="xl" />
          {activity.dueDate && (
            <>
              <CustomText text="Due Date" weight="ultraBold" size="m" style={{ color: "#182230" }} />
              <CustomSpacing type="vertical" size="xl" />
              <CustomText
                useTranslationText={false}
                text={new Date(activity.dueDate).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
                size="m"
                style={styles.text}
              />
              <CustomSpacing type="vertical" size="xl" />
            </>
          )}
          <CustomText text="Interactive Learning Experience" weight="ultraBold" size="m" style={{ color: "#182230" }} />
          <CustomSpacing type="vertical" size="xl" />
          {activity?.aiTutors?.map((tutor) => (
            <View key={tutor.id}>
              <CustomText text={tutor.title} weight="normal" size="m" style={styles.text} useTranslationText={false} />
              <CustomSpacing type="vertical" size="xl" />
            </View>
          ))}
        </>
      )}

      {/* === Project Specific Info === */}
      {activity.type === "Project" && (
        <>
          <CustomSpacing type="vertical" size="xl" />
          {activity.scopeDocument && (
            <>
              <CustomText text="Scope Document" weight="ultraBold" size="m" style={{ color: "#182230" }} />
              <CustomSpacing type="vertical" size="xl" />
              <CustomText
                text={activity.scopeDocument}
                useTranslationText={false}
                style={styles.text}
                weight="normal"
              />
              <CustomSpacing type="vertical" size="xl" />
            </>
          )}
          {activity.deliverableTypes?.length > 0 && (
            <>
              <CustomText text="Deliverable Types" weight="ultraBold" size="m" style={{ color: "#182230" }} />
              <CustomSpacing type="vertical" size="xl" />
              {activity.deliverableTypes.map((constraint, index) => (
                <CustomText
                  key={index}
                  text={`- ${constraint}`}
                  useTranslationText={false}
                  style={styles.text}
                  weight="normal"
                />
              ))}
              <CustomSpacing type="vertical" size="xl" />
            </>
          )}
          {activity.assessmentCriteria?.length > 0 && (
            <>
              <CustomText text="Assessment Criteria" weight="ultraBold" size="m" style={{ color: "#182230" }} />
              <CustomSpacing type="vertical" size="xl" />
              {activity.assessmentCriteria.map((criteria, index) => (
                <CustomText
                  key={index}
                  text={`- ${criteria}`}
                  useTranslationText={false}
                  style={styles.text}
                  weight="normal"
                />
              ))}
              <CustomSpacing type="vertical" size="xl" />
            </>
          )}
          {activity.expectedOutcomes?.length > 0 && (
            <>
              <CustomText text="Expected Outcomes" weight="ultraBold" size="m" style={{ color: "#182230" }} />
              <CustomSpacing type="vertical" size="xl" />
              {activity.expectedOutcomes.map((outcome, index) => (
                <CustomText
                  key={index}
                  text={`- ${outcome}`}
                  useTranslationText={false}
                  style={styles.text}
                  weight="normal"
                />
              ))}
              <CustomSpacing type="vertical" size="xl" />
            </>
          )}
          {activity.status && (
            <>
              <CustomText text="Status" weight="ultraBold" size="m" style={{ color: "#182230" }} />
              <CustomSpacing type="vertical" size="xl" />
              <CustomText text={activity.status} useTranslationText={false} style={styles.text} weight="normal" />
              <CustomSpacing type="vertical" size="xl" />
            </>
          )}
          {activity.dueDate && (
            <>
              <CustomText text="Due Date" weight="ultraBold" size="m" style={{ color: "#182230" }} />
              <CustomSpacing type="vertical" size="xl" />
              <CustomText
                useTranslationText={false}
                text={new Date(activity.dueDate).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
                size="m"
              />
              <CustomSpacing type="vertical" size="xl" />
            </>
          )}
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  activityHeader: {
    alignItems: "center",
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    justifyContent: "space-between",
    marginVertical: 10,
    paddingBottom: 20,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignContent: "center",
  },
  text: {
    color: "#344054",
  },
  fileContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  fileText: {
    color: "#344054",
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F9FAFB",
  },
  emptyText: {
    color: "#6B7280",
  },
  visualizationContainer: {
    marginTop: 16,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#EAECF0",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  webViewContainer: {
    height: 700,
    width: "100%",
  },
  webView: {
    flex: 1,
    backgroundColor: "#EAECF0",
  },
  videoRow: {
    flexDirection: "row",
    gap: 16,
    paddingVertical: 4,
  },
  videoCard: {
    backgroundColor: "#FFFFFF",
    padding: 12,
    borderRadius: 12,
    borderColor: "#EAECF0",
    borderWidth: 1,
    marginRight: 16,
    width: 320,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  webview: {
    width: "100%",
    height: 180,
    borderRadius: 8,
    overflow: "hidden",
  },
  thumbnailImage: {
    width: "100%",
    height: 180,
    borderRadius: 8,
    objectFit: "cover",
  },
  thumbnailButton: {
    padding: 0,
    borderWidth: 0,
    backgroundColor: "transparent",
    width: "100%",
  },
  generalButtonStyle: {
    padding: 0,
    paddingHorizontal: 12,
    paddingVertical: 8,
    height: BUTTON_HEIGHT,
    borderColor: "#EAECF0",
  },
});

export default ActivityContainer;
